import { DataService } from '../services/data_service';
import { EntryComponentConfig, Envelope } from '../shared/types/common.model';
import { ClientRequestHandler } from './client-request-handler';
import {
  EnrollmentType,
  LearningJourneyContext,
  LearningJourneyContextSchema,
  LearningJourneyState,
  LearningJourneyStateSchema,
} from '../shared/types/learning-journeys.model';

export class LearningJourneysClient extends ClientRequestHandler {
  private dataService: DataService;

  constructor(internalConfig: EntryComponentConfig) {
    super('LearningJourneysClientError');
    this.dataService = DataService.getInstance(internalConfig.baseUrl);
  }

  /**
   * Fetches Learning Journey Context for a given enrollment type and id
   * @param userId - The user ID
   * @param enrollmentType - LEARNING_JOURNEY_ENROLLMENT_TYPE or COURSE_ENROLLMENT_TYPE
   * @param id - The enrollment ID (journey or course enrollment ID)
   * @param signal - Optional AbortSignal
   * @returns Promise<LearningJourneyContext>
   */
  async getLearningJourneyContext(
    userId: number,
    enrollmentType: EnrollmentType,
    id: string | number,
    signal?: AbortSignal
  ): Promise<LearningJourneyContext> {
    const url = `/angie/users/${userId}/learning-journeys/context/${enrollmentType}/${id}.json`;
    const request = this.dataService.get<Envelope<LearningJourneyContext>>(url, signal);
    return this.handleRequest<LearningJourneyContext>(request, LearningJourneyContextSchema);
  }

  /**
   * Fetches the state of a learning journey for a specific user and enrollment
   * (Currently returns mock data until backend is ready)
   * @param userId - The user ID
   * @param externalWorkflowId - Learning Journey external workflow ID
   * @param versionId - The version ID
   * @param externalEnrollmentId - Learning Journey external enrollment ID
   * @param signal - Optional AbortSignal
   * @returns Promise<LearningJourneyState>
   */
  async getLearningJourneyState(
    userId: number,
    externalWorkflowId: string,
    versionId: string,
    externalEnrollmentId: string,
    signal?: AbortSignal
  ): Promise<LearningJourneyState> {
    const url = `/angie/users/${userId}/learning-journeys/${externalWorkflowId}/${versionId}/${externalEnrollmentId}/state.json`;
    const request = this.dataService.get<Envelope<LearningJourneyState>>(url, signal);
    return this.handleRequest<LearningJourneyState>(request, LearningJourneyStateSchema);
  }
}
